import React, { ComponentPropsWithoutRef, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { isValid } from 'date-fns';
import { format, parseISO } from 'date-fns';
import { useFormikContext } from 'formik';
import _ from 'lodash';

import { useInjection } from '@context/inversify-context-provider';
import { TYPES } from '@libs/InversifyType';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { RootState } from '@store/rootReducer';

import { AamBackendApi } from '../../../../libs/aamBackendApi';
import { locales, textErrors } from '../../helpers';
import { useT } from '../../hooks/useT';
import { FormControlLoader } from '../FormControlLoader';

type MUIDatePickerProps = ComponentPropsWithoutRef<typeof DatePicker>;

export type OODatePickerProps = Omit<
  MUIDatePickerProps,
  'value' | 'onChange' | 'slots' | 'slotProps' | 'defaultValue'
> & {
  /** ISO-8601 (YYYY-MM-DD) to seed Formik when undefined */
  defaultValue?: string;
  headerTextFormat?: string;
  validationKey: string;
  label: string;
  minOffset?: number;
  maxOffset?: number;
  isMandatory: boolean;
  name: string;
  onChangeCallback?: (event: any) => void;
};

export const OODatePicker: React.FC<OODatePickerProps> = ({
  headerTextFormat,
  validationKey,
  label,
  isMandatory,
  minOffset,
  maxOffset,
  onChangeCallback,
  defaultValue,
  name,
  ...props
}) => {
  const { t, translationsLoading, i18n } = useT('entry', 'documents');
  const formik = useFormikContext<Record<string, any>>();
  const fieldProps = formik.getFieldProps(validationKey);
  const fieldMeta = formik.getFieldMeta(validationKey);
  const value = fieldProps.value;
  const prefix = label?.split('.')[0].toLowerCase();

  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const [dateFormat, dateFormatSet] = useState<string>('dd-MM-yyyy');

  const { selectedConfigurationId, clientOrganizationId, importClientOrganizationId } = useSelector(
    (state: RootState) => state.oneOnboarding,
  );
  const selectedLanguage = localStorage.getItem('i18nextLng');
  const clientOrgId = clientOrganizationId || importClientOrganizationId;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (clientOrgId && selectedConfigurationId) {
      const fetchDisplayDateFormat = async () => {
        const configData = await aamBackendApi.getOtherConfiguration(clientOrgId, selectedConfigurationId);
        const defaultLanguage = configData?.find((d) => d.name === 'languages')?.value;
        const fallbackVerification = configData?.find((d) => d.name === 'displayDateFormatFallback')?.value;

        const format = fallbackVerification
          ? configData?.find((d) =>
              selectedLanguage === defaultLanguage
                ? d.name === 'displayDateFormat'
                : d.name === 'displayDateFormatFallback',
            )?.value
          : configData?.find((d) => d.name === 'displayDateFormat')?.value;
        if (format) {
          dateFormatSet(format);
        }
      };
      fetchDisplayDateFormat();
    }
  }, [clientOrgId, selectedConfigurationId, aamBackendApi, selectedLanguage]);

  const maxDate = useMemo(() => {
    if (maxOffset || maxOffset === 0) {
      const date = new Date();
      date.setDate(date.getDate() + maxOffset);
      return date;
    }
    return undefined;
  }, [maxOffset]);

  const minDate = useMemo(() => {
    if (minOffset || minOffset === 0) {
      const date = new Date();
      date.setDate(date.getDate() + minOffset);
      return date;
    }
    return undefined;
  }, [minOffset]);

  const setFormikFieldValue = useEffectEvent(() => {
    formik.setFieldValue(validationKey, defaultValue);
  });

  useEffect(() => {
    if (defaultValue && fieldProps.value === undefined) {
      setFormikFieldValue();
    }
  }, [defaultValue, fieldProps.value]);

  const datepickerValue: Date | null = useMemo(() => {
    if (_.isEmpty(value)) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }, [value]);

  if (translationsLoading) {
    return <FormControlLoader />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[i18n.language]}>
      <DatePicker
        enableAccessibleFieldDOMStructure={false}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={datepickerValue}
        label={t(`${prefix}:${label}`)}
        format={dateFormat}
        maxDate={maxDate}
        minDate={minDate}
        onChange={(next) => {
          const formatedDate = next ? format(next as Date, 'yyyy-MM-dd') : next;
          formik.setFieldValue(validationKey, formatedDate);
          onChangeCallback && onChangeCallback(formatedDate);
        }}
        slots={{ textField: TextField }}
        slotProps={{
          textField: {
            required: isMandatory,
            name,
            onBlur: fieldProps.onBlur,
            onClick: () => setOpen(true),
            placeholder: t(`${prefix}:${label}`),
            label: t(`${prefix}:${label}`),
            inputProps: { placeholder: t(`${prefix}:${label}`) },
            ...textErrors(fieldMeta),
          },
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};
